import React, { useMemo, useState } from 'react';
import { Card, Form, Select, DatePicker, Checkbox, Button, Space, Table, Typography, Alert, Modal } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

type AtomicUserType = 'h5注册用户' | '安卓app注册用户' | 'ios app注册用户' | '充值用户' | '首充用户';
type UserType = AtomicUserType | 'app注册用户' | '注册用户（全量）';
type RetentionType = '活跃留存' | '投注留存' | '充值留存';

interface RetentionRow {
  key: string;
  日期: string;
  用户类型: UserType;
  用户人数: number;
  // 动态留存列：k日留存 -> 值
  [k: string]: any;
}

const { RangePicker } = DatePicker;
const { Title } = Typography;

function seededRandom(seed: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // 归一化到(0,1)
  return (h >>> 0) / 0xffffffff;
}

function genCountsAtomic(date: string, userType: AtomicUserType, retentionType: RetentionType) {
  const seed = `${date}|${userType}|${retentionType}`;
  const r1 = seededRandom(seed);
  const r2 = seededRandom(seed + '#1');
  const r3 = seededRandom(seed + '#2');
  // 基础人数（更贴近真实）：H5注册 > APP注册 > 首充/充值
  const baseRanges: Record<AtomicUserType, [number, number]> = {
    'h5注册用户': [120, 320],
    '安卓app注册用户': [50, 140],
    'ios app注册用户': [40, 120],
    '充值用户': [60, 180],
    '首充用户': [30, 100],
  };
  const [minB, maxB] = baseRanges[userType];
  const base = Math.floor(minB + r1 * (maxB - minB));

  // 留存率基线（更贴近真实）
  const rateBase: Record<RetentionType, { d1: [number, number]; d2: [number, number] }> = {
    '活跃留存': { d1: [0.35, 0.60], d2: [0.20, 0.45] },
    '投注留存': { d1: [0.18, 0.40], d2: [0.10, 0.30] },
    '充值留存': { d1: [0.45, 0.75], d2: [0.30, 0.60] },
  };
  const [d1min, d1max] = rateBase[retentionType].d1;
  const [d2min, d2max] = rateBase[retentionType].d2;

  // 不同用户类型对留存的影响因子
  const userTypeFactor: Record<AtomicUserType, number> = {
    'h5注册用户': 0.95,
    '安卓app注册用户': 1.00,
    'ios app注册用户': 1.02,
    '充值用户': 1.05,
    '首充用户': 1.00,
  };

  let r1d = (d1min + r2 * (d1max - d1min)) * userTypeFactor[userType];
  let r2d = (d2min + r3 * (d2max - d2min)) * userTypeFactor[userType];
  r1d = Math.max(0, Math.min(0.98, r1d));
  r2d = Math.max(0, Math.min(0.98, r2d, r1d - 0.02));
  return { base, r1d, r2d };
}

function getAtomicTypes(userType: UserType): AtomicUserType[] {
  if (userType === 'app注册用户') return ['安卓app注册用户', 'ios app注册用户'];
  if (userType === '注册用户（全量）') return ['h5注册用户', '安卓app注册用户', 'ios app注册用户'];
  return [userType as AtomicUserType];
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(2)}%`;
}

function genKDayRetentionRate(date: string, userType: UserType, retentionType: RetentionType, k: number, r1d: number, r2d: number): number {
  if (k <= 0) return 1;
  if (k === 1) return r1d;
  if (k === 2) return Math.max(0, Math.min(r2d, r1d - 0.02));
  // 3日及以后，按照温和衰减：前一日 - [0.5%, 2.5%] 区间的随机数，但不小于0
  const prev = genKDayRetentionRate(date, userType, retentionType, k - 1, r1d, r2d);
  const dec = 0.005 + seededRandom(`${date}|${userType}|${retentionType}|k=${k}`) * 0.02;
  return Math.max(0, Math.min(prev - dec, prev));
}

const RetentionReports: React.FC = () => {
  const [form] = Form.useForm();
  const [showCount, setShowCount] = useState<boolean>(true);
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [columnModalVisible, setColumnModalVisible] = useState<boolean>(false);
  const [tempDays, setTempDays] = useState<number[]>([]);

  // 默认日期范围：当年10月1日-10月28日
  const defaultRange = useMemo(() => {
    const year = dayjs().year();
    return [dayjs(`${year}-10-01`), dayjs(`${year}-10-28`)];
  }, []);

  const [range, setRange] = useState<[Dayjs, Dayjs]>([defaultRange[0], defaultRange[1]]);
  const [userType, setUserType] = useState<UserType>('充值用户');
  const [retentionType, setRetentionType] = useState<RetentionType>('活跃留存');
  // 实时“今天”
  const assumedToday = dayjs().startOf('day');

  // 动态列数量：根据日期范围天数，最大90
  const horizonDays = useMemo(() => {
    if (!range || !range[0] || !range[1]) return 0;
    const start = range[0].startOf('day');
    const end = range[1].startOf('day');
    const days = end.diff(start, 'day') + 1; // 包含端点
    return Math.min(90, Math.max(1, days));
  }, [range]);

  const data: RetentionRow[] = useMemo(() => {
    const rows: RetentionRow[] = [];
    if (!range || !range[0] || !range[1]) return rows;
    const start = range[0].startOf('day');
    const end = range[1].startOf('day');
    const totalDays = end.diff(start, 'day');
    for (let i = totalDays; i >= 0; i--) { // 倒序：最新日期在最前面
      const d = start.add(i, 'day');
      const dateStr = d.format('YYYY-MM-DD');
      // 组合类型：基于原子类型聚合基础人数与留存
      const atoms = getAtomicTypes(userType);
      const compBases = atoms.map(a => genCountsAtomic(dateStr, a, retentionType).base);
      const base = compBases.reduce((s, v) => s + v, 0);
      const row: RetentionRow = {
        key: dateStr,
        日期: dateStr,
        用户类型: userType,
        用户人数: base,
      };
      const kList = (selectedDays && selectedDays.length > 0)
        ? [...selectedDays].sort((a, b) => a - b)
        : Array.from({ length: horizonDays }, (_, idx) => idx + 1);
      for (const k of kList) {
        const occurDate = d.add(k, 'day').startOf('day');
        if (occurDate.isAfter(assumedToday)) {
          row[`${k}日留存`] = '-';
          continue;
        }
        if (occurDate.isSame(assumedToday)) {
          // 任意k日留存，只要发生日在今天，则展示正常值的一半
          let totalKept = 0;
          atoms.forEach(a => {
            const { base: b, r1d, r2d } = genCountsAtomic(dateStr, a, retentionType);
            const rate = genKDayRetentionRate(dateStr, a, retentionType, k, r1d, r2d);
            totalKept += Math.max(0, Math.floor(b * rate / 2));
          });
          const rateHalf = base > 0 ? totalKept / base : 0;
          row[`${k}日留存`] = showCount ? `${totalKept}/${formatPercent(rateHalf)}` : `${formatPercent(rateHalf)}`;
          continue;
        }
        // 历史日期：按原子类型聚合
        let totalKept = 0;
        atoms.forEach(a => {
          const { base: b, r1d, r2d } = genCountsAtomic(dateStr, a, retentionType);
          const rate = genKDayRetentionRate(dateStr, a, retentionType, k, r1d, r2d);
          totalKept += Math.max(0, Math.floor(b * rate));
        });
        const rate = base > 0 ? totalKept / base : 0;
        row[`${k}日留存`] = showCount ? `${totalKept}/${formatPercent(rate)}` : `${formatPercent(rate)}`;
      }
      rows.push(row);
    }
    return rows;
  }, [range, userType, retentionType, showCount, horizonDays, assumedToday, selectedDays]);

  const columns = useMemo(() => {
    const baseCols = [
      { title: '日期', dataIndex: '日期', key: '日期', width: 120, fixed: 'left' as const, align: 'center' as const },
      { title: '用户类型', dataIndex: '用户类型', key: '用户类型', width: 110, fixed: 'left' as const, align: 'center' as const },
      { title: '用户人数', dataIndex: '用户人数', key: '用户人数', width: 100, fixed: 'left' as const, align: 'center' as const },
    ];
    const list = (selectedDays && selectedDays.length > 0)
      ? [...selectedDays].sort((a, b) => a - b)
      : Array.from({ length: horizonDays }, (_, idx) => idx + 1);
    const dynamicCols = list.map(dayNum => ({
      title: `${dayNum}日留存`,
      dataIndex: `${dayNum}日留存`,
      key: `${dayNum}日留存`,
      width: 100,
      align: 'center' as const,
    }));
    return [...baseCols, ...dynamicCols];
  }, [horizonDays, selectedDays]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>留存报表</Title>
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="inline"
          initialValues={{
            userType: userType,
            retentionType: retentionType,
            range: [range[0], range[1]],
            showCount: showCount,
            selectedDays: selectedDays,
          }}
          onValuesChange={(changed, all) => {
            if (changed.userType) setUserType(changed.userType as UserType);
            if (changed.retentionType) setRetentionType(changed.retentionType as RetentionType);
            if (changed.range) {
              const r = changed.range as [Dayjs, Dayjs];
              if (r && r[0] && r[1]) setRange([r[0], r[1]]);
            }
            if (typeof changed.showCount === 'boolean') setShowCount(changed.showCount);
            if (changed.selectedDays) {
              const v = (changed.selectedDays as number[]).filter(n => Number.isFinite(n) && n > 0 && n <= 90);
              setSelectedDays(v);
            }
          }}
        >
          <Form.Item label="用户类型" name="userType" required>
            <Select style={{ width: 200 }}
              options={[
                { label: '注册用户（全量）', value: '注册用户（全量）' },
                { label: 'h5注册用户', value: 'h5注册用户' },
                { label: 'app注册用户', value: 'app注册用户' },
                { label: '安卓app注册用户', value: '安卓app注册用户' },
                { label: 'ios app注册用户', value: 'ios app注册用户' },
                { label: '充值用户', value: '充值用户' },
                { label: '首充用户', value: '首充用户' },
              ]}
            />
          </Form.Item>
          <Form.Item label="留存类型" name="retentionType" required>
            <Select style={{ width: 160 }}
              options={[
                { label: '活跃留存', value: '活跃留存' },
                { label: '投注留存', value: '投注留存' },
                { label: '充值留存', value: '充值留存' },
              ]}
            />
          </Form.Item>
          <Form.Item label="日期范围" name="range" required>
            <RangePicker allowClear={false} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item>
            <Button onClick={() => {
              const list = Array.from({ length: horizonDays }, (_, i) => i + 1);
              setTempDays((selectedDays && selectedDays.length > 0) ? [...selectedDays] : list);
              setColumnModalVisible(true);
            }}>选择留存列</Button>
          </Form.Item>
          <Form.Item name="showCount" valuePropName="checked">
            <Checkbox>展示人数</Checkbox>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={() => { /* 数据为计算属性，点击仅用于交互一致性 */ }}>查询</Button>
              <Button onClick={() => {
                form.setFieldsValue({
                  userType: '充值用户',
                  retentionType: '活跃留存',
                  range: [defaultRange[0], defaultRange[1]],
                  showCount: true,
                  selectedDays: [],
                });
                setUserType('充值用户');
                setRetentionType('活跃留存');
                setRange([defaultRange[0], defaultRange[1]]);
                setShowCount(true);
                setSelectedDays([]);
              }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
        {horizonDays >= 90 && (
          <div style={{ marginTop: 12 }}>
            <Alert showIcon type="info" message="当前留存列已达最大支持90天，横向滚动可查看全部列" />
          </div>
        )}
      </Card>

      <Card>
        <Table
          rowKey="key"
          size="small"
          dataSource={data}
          columns={columns}
          pagination={{ pageSize: 20 }}
          sticky
          scroll={{ x: Math.max(600, 330 + (selectedDays && selectedDays.length > 0 ? selectedDays.length : horizonDays) * 100), y: undefined }}
        />
      </Card>

      <Modal
        title="选择留存列"
        open={columnModalVisible}
        onCancel={() => setColumnModalVisible(false)}
        onOk={() => {
          setSelectedDays([...tempDays].sort((a, b) => a - b));
          setColumnModalVisible(false);
        }}
      >
        <Space style={{ marginBottom: 12 }}>
          <Button onClick={() => setTempDays(Array.from({ length: horizonDays }, (_, i) => i + 1))}>全选（当前范围）</Button>
          <Button onClick={() => setTempDays([])}>清空</Button>
        </Space>
        <div style={{ maxHeight: 360, overflowY: 'auto', paddingRight: 8 }}>
          <Checkbox.Group
            style={{ width: '100%' }}
            value={tempDays}
            onChange={(vals) => setTempDays((vals as number[]).filter(n => Number.isFinite(n) && n > 0 && n <= 90))}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
              {Array.from({ length: 90 }, (_, i) => i + 1).map((n) => (
                <Checkbox key={n} value={n}>{n}日</Checkbox>
              ))}
            </div>
          </Checkbox.Group>
          <div style={{ color: '#999', marginTop: 8 }}>提示：不选择表示按日期范围自动展示 1..N（最多90天）。</div>
        </div>
      </Modal>
    </div>
  );
};

export default RetentionReports;


